import React, { forwardRef, useRef, useState, useEffect, useImperativeHandle, useCallback } from "react"
import Textarea from "react-textarea-autosize"

import { getTimeUtc, getSmallestStartDate, getGreatestEndDate } from "../utils/dates"
import { toast } from "react-toastify"
import startOfDay from "date-fns/startOfDay"
import { endOfDay } from "date-fns/endOfDay"
import apiOrg from "../services/org/org.api"
import groupBy from "lodash/groupBy"

import { v4 as uniqid } from "uuid"

import { ensureNumber, isArchived } from "../utils/utils"

export const crmHoursCheck = ({ params, gtRoleDataRef, orgData, context, app }) => {
    let approvedCrmHours = 0
    let providedMissionXHours = 0

    if (!params.node.level) {
        return {
            issue: false,
            approvedCrmHours,
            providedMissionXHours,
        }
    }

    let isRole = params.node.level === 2

    //const key = params.node.level === 1 ? params.node.key : ""

    let myRolePlantItems = []
    let myPlanItems = []

    gtRoleDataRef.current.forEach((gtr) => {
        if (gtr.programId && !gtr.mergedTo) {
            if (isRole && gtr.roleId !== params.node.key) {
                return
            }
            if (!isRole && gtr.programId !== params.node.key) {
                return
            }

            myRolePlantItems.push(gtr)

            if (gtr.createdFromGtCrm) {
                const opi = gtr.snapshot ? gtr.snapshot.planItem : JSON.parse(gtr.metaData).planItem

                myPlanItems.push(opi)
            }
        }
    })

    approvedCrmHours = myRolePlantItems.reduce((cumm, obj) => (cumm += ensureNumber(obj.hoursApproved)), 0)
    providedMissionXHours = myPlanItems.reduce((cumm, obj) => (cumm += ensureNumber(obj.hoursNeeded)), 0)

    return {
        issue: approvedCrmHours !== providedMissionXHours,
        approvedCrmHours,
        providedMissionXHours,
    }
}

export function gtChangeStatus({ rolesData, orgData, cb }) {
    apiOrg
        .changeRolePlanItemStatus(orgData._id, rolesData)
        .then((res) => {
            cb(res.map((d) => d.document))
        })
        .catch((err) => {
            toast.error(err.message)
        })
}

export function doRolesRequireApproval(mission, orgData) {
    const missionDepartment = orgData.departments?.find((d) => !isArchived(d) && d._id === mission.departmentTags[0])
    const missionOffice = orgData.offices?.find((o) => o._id === mission.office)

    const isIn =
        mission.createdFromGtCrm &&
        missionDepartment?.title === "Assurance" &&
        missionOffice?._id === "66e3db6b0adca66cf7368435" &&
        !mission.customProperties?.find((c) => c.value?.includes("BGA"))

    return isIn
}

const TellUsWhy = forwardRef((props, ref) => {
    const reason = useRef("")
    const setReason = useCallback((v) => {
        reason.current = v
    }, [])

    const getReason = useCallback((v) => {
        return reason.current
    }, [])

    useImperativeHandle(ref, () => ({
        getReason,
    }))

    useEffect(() => {
        return () => {
            setReason("")
        }
    }, [])

    return (
        <div>
            <p>{props.msg || "Please tell us why you need this new role:"}</p>
            <Textarea
                type="text"
                onChange={(e) => {
                    setReason(e.target.value)
                }}
            />
        </div>
    )
})

export function useAddGtRole() {
    const reasonRef = useRef()
    const [open, setOpen] = useState(false)

    const addGtRole = ({ roleData, orgData, app, cb, context, isSplit }) => {
        setOpen(true)

        const doSave = (messages) => {
            let saveObject = {
                ...roleData,
                messages: messages,
                context: context,
                status: "draft",
            }

            apiOrg
                .gtCreateRolePlanItem(orgData._id, saveObject)
                .then((res) => {
                    cb(res)
                })
                .catch((err) => {
                    toast.error(err.message)
                })
        }

        if (isSplit) {
            doSave()
        } else {
            app.confirm({
                open: open,
                comp: <TellUsWhy ref={reasonRef} />,
                yesText: "Create role",
                singleClick: true,
                onYes: () => {
                    const myReason = reasonRef.current.getReason()

                    if (myReason.length < 5) {
                        toast("Please enter a longer description")
                        return false
                    }

                    setOpen(false)
                    doSave([
                        {
                            msg: myReason,
                            createdBy: app.state.person._id,
                            createdOn: getTimeUtc(new Date()),
                        },
                    ])
                },
            })
        }
    }

    return addGtRole
}
export function useGetGtComment() {
    const reasonRef = useRef()
    const [open, setOpen] = useState(false)

    const getGtRoleMsg = ({ app, cb, msg, buttonText }) => {
        setOpen(true)

        app.confirm({
            open: open,
            comp: <TellUsWhy ref={reasonRef} msg={msg} />,
            yesText: buttonText || "Submit",
            singleClick: true,
            onYes: () => {
                if (reasonRef.current.getReason()?.length < 5) {
                    toast("Please enter a longer description")
                    return false
                }

                setOpen(false)
                cb(reasonRef.current.getReason())
            },
        })
    }

    return getGtRoleMsg
}

export function updateGtRole({ roles, role, orgData, cb }) {
    if (role) {
        roles = [role]
    }

    apiOrg
        .gtUpdateRolePlanItem(orgData._id, roles)
        .then((res) => {
            cb(res.map((d) => d.document))
        })
        .catch((err) => {
            toast.error("An error occured. Please contact us for more info. " + err.message)
        })
}
export function gtRemoveOrgRole({ role, orgData, app, cb }) {
    apiOrg
        .gtDeleteRolePlanItem(orgData._id, role._id)
        .then((res) => {
            cb(role)
        })
        .catch((err) => {
            toast.error("An issue occured. Please contact us. help@missionx.ai " + err.message)
        })
}

export async function gtMergeTwoRoles({ roleToBeMerged, roleToAccept, orgData, app, cb, context }) {
    app.block()

    let toBeMergedChange = {
        _id: roleToBeMerged._id,
        mergedTo: roleToAccept._id,
    }
    const hours = roleToBeMerged.hoursApproved + roleToAccept.hoursApproved

    let toAcceptMergedChange = {
        _id: roleToAccept._id,
        hoursApproved: hours,
    }

    const change1 = await apiOrg.gtUpdateRolePlanItem(orgData._id, [toAcceptMergedChange])

    const change2 = await apiOrg.gtUpdateRolePlanItem(orgData._id, [toBeMergedChange])

    app.unBlock()

    cb([change2[0].document, change1[0].document])
}

export async function gtMergeRoles({ roles, orgData, app, cb, context }) {
    app.block("Please wait while roles are merged. Please do not navigate away.")
    const groupByProgram = groupBy(roles, "programId")

    let rolesToCreate = []
    let rolesToUpdateData = []

    Object.keys(groupByProgram).forEach((programID) => {
        const myProgramRoles = groupByProgram[programID]

        const uniqueRoles = groupBy(myProgramRoles, "roleId")

        Object.keys(uniqueRoles).forEach((key) => {
            const myRoles = uniqueRoles[key]

            const start = getSmallestStartDate(myRoles)
            const end = getGreatestEndDate(myRoles)
            const ourRefId = uniqid()

            const nr = {
                context: context,
                startDate: getTimeUtc(startOfDay(start)),
                endDate: getTimeUtc(endOfDay(end)),
                hoursApproved: myRoles.reduce((cumm, obj) => cumm + obj.hoursApproved, 0),
                hoursNeeded: 0,
                missionId: "merged",
                programId: programID,
                roleId: key,
                mergeReferenceId: ourRefId,
            }
            rolesToCreate.push(nr)

            myRoles.forEach((item, i) => {
                rolesToUpdateData.push({ ...item, mergeReferenceId: ourRefId }) //used to link them after create
            })
        })
    })

    const planItemResults = await Promise.allSettled(
        rolesToCreate.map((nr) => {
            return apiOrg.gtCreateRolePlanItem(orgData._id, nr)
        })
    )

    let planItemsCreated = []
    let notCreated = []

    planItemResults.forEach((result, i) => {
        if (result.status === "fulfilled" && result.value) {
            planItemsCreated.push(result.value)
        } else {
            notCreated.push(result)
        }
    })

    let rolesToUpdateNew = []

    planItemsCreated.forEach((pc, i) => {
        const updateThese = rolesToUpdateData.filter((ru) => ru.mergeReferenceId === pc.mergeReferenceId)

        updateThese.forEach((up, i) => {
            rolesToUpdateNew.push({
                _id: up._id,
                mergedTo: pc._id,
                mergedOn: getTimeUtc(new Date()),
                mergedBy: app.state.person._id,
            })
        })
    })

    apiOrg
        .gtUpdateRolePlanItem(orgData._id, rolesToUpdateNew)
        .then((resx) => {
            app.unBlock()
            toast.success("Your approved hours were combined. ")

            cb([...planItemsCreated, ...resx.map((d) => d.document)])
        })
        .catch((errx) => {
            app.silentFail(errx.message, "gt-allocation")
            app.unBlock()
            toast.error(errx.message)
        })
}
